import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
  IconButton,
  Button,
  TextField,
  Divider,
  Paper,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { Add, Remove, DeleteOutline } from "@mui/icons-material";
import { useCart } from "../context/CartContext";
import { useNavigate } from "react-router-dom";
import EmptyCart from "./EmptyCart";

export default function Order() {
  const { cart, dispatch } = useCart();
  const navigate = useNavigate();
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [openTerms, setOpenTerms] = useState(false);
  const [customer, setCustomer] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
  });

  const totalBGN = cart.reduce((sum, i) => sum + i.qty * i.priceBGN, 0);
  const totalEUR = cart.reduce((sum, i) => sum + i.qty * i.priceEUR, 0);

  const updateQty = (id, qty) => {
    if (qty < 1) return;
    dispatch({ type: "UPDATE_QTY", id, qty });
  };

  const removeItem = (id) => {
    dispatch({ type: "REMOVE", id });
  };

  const handleChange = (e) => {
    setCustomer({ ...customer, [e.target.name]: e.target.value });
  };

  // --- SEND ORDER TO PHP AND REDIRECT ---
  const submitOrder = async (e) => {
    e.preventDefault();

    if (!acceptedTerms) {
      alert("Моля, приемете Общите условия, за да завършите поръчката.");
      return;
    }
    if (!customer.name || !customer.email || !customer.phone) {
      alert("Моля, попълнете всички задължителни полета (Име, Имейл, Телефон).");
      return;
    }
    if (cart.length === 0) {
      alert("Количката е празна.");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("name", customer.name);
      formData.append("email", customer.email);
      formData.append("phone", customer.phone);
      formData.append("address", customer.address);
      formData.append("cart", JSON.stringify(cart));

      const response = await fetch("/api/sendOrder.php", {
        method: "POST",
        body: formData,
      });

      const result = await response.json();

      if (result.status === "success") {
        dispatch({ type: "CLEAR" });
        navigate("/thank-you"); // пренасочване към Thank You
      } else {
        alert("Грешка при изпращане на поръчката: " + result.msg);
      }
    } catch (error) {
      console.error(error);
      alert("Грешка при връзка със сървъра.");
    }
  };

  if (cart.length === 0) return <EmptyCart />;

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", p: 3 }}>
      <Typography variant="h4" mb={3}>🛒 Вашата количка</Typography>

      <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "2fr 1fr" }} gap={3}>
        {/* CART ITEMS */}
        <Box>
          {cart.map((item) => (
            <Card key={item.id} sx={{ mb: 2 }} elevation={2}>
              <CardContent sx={{ display: "flex", gap: 2 }}>
                <CardMedia
                  component="img"
                  src={item.image}
                  alt={item.name}
                  sx={{ width: 90, borderRadius: 1 }}
                />
                <Box flex={1}>
                  <Typography fontWeight="bold">{item.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Опаковка: {item.pack}
                  </Typography>
                  <Box display="flex" alignItems="center" mt={1}>
                    <IconButton onClick={() => updateQty(item.id, item.qty - 1)}><Remove /></IconButton>
                    <Typography>{item.qty}</Typography>
                    <IconButton onClick={() => updateQty(item.id, item.qty + 1)}><Add /></IconButton>
                  </Box>
                </Box>
                <Box textAlign="right">
                  <Typography fontWeight="bold">{(item.qty * item.priceBGN).toFixed(2)} лв</Typography>
                  <Typography variant="body2" color="text.secondary">{(item.qty * item.priceEUR).toFixed(2)} €</Typography>
                  <IconButton onClick={() => removeItem(item.id)}><DeleteOutline /></IconButton>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>

        {/* CHECKOUT */}
        <Paper elevation={3} sx={{ p: 3 }}>
          <Typography variant="h6">Данни за поръчка</Typography>
          <Divider sx={{ my: 2 }} />

          <form onSubmit={submitOrder}>
            <TextField fullWidth required label="Име" name="name" value={customer.name} onChange={handleChange} margin="dense" />
            <TextField fullWidth required label="Имейл" name="email" type="email" value={customer.email} onChange={handleChange} margin="dense" />
            <TextField fullWidth required label="Телефон" name="phone" value={customer.phone} onChange={handleChange} margin="dense" />
            <TextField fullWidth label="Адрес" name="address" value={customer.address} onChange={handleChange} margin="dense" />

            <Divider sx={{ my: 2 }} />

            <Typography fontWeight="bold">Общо: {totalBGN.toFixed(2)} лв</Typography>
            <Typography variant="body2">({totalEUR.toFixed(2)} €)</Typography>

            <FormControlLabel
              control={<Checkbox checked={acceptedTerms} onChange={(e) => setAcceptedTerms(e.target.checked)} color="primary" />}
              label={<span>Приемам <Button type="button" variant="text" onClick={() => setOpenTerms(true)} sx={{ p: 0, textTransform: "none" }}>Общите условия</Button></span>}
              sx={{ mt: 2 }}
            />

            <Button type="submit" variant="contained" fullWidth sx={{ mt: 2, py: 1.2 }} disabled={!acceptedTerms}>Потвърди поръчката</Button>
          </form>
        </Paper>
      </Box>

      {/* POPUP TERMS */}
      <Dialog open={openTerms} onClose={() => setOpenTerms(false)} maxWidth="md" fullWidth>
        <DialogTitle>Общи условия</DialogTitle>
        <DialogContent dividers>
          <Typography gutterBottom><strong>Общи условия на онлайн магазин TC Energy</strong></Typography>
          <Typography gutterBottom>Настоящите Общи условия уреждат отношенията между онлайн магазина TC Energy и всеки потребител...</Typography>
          {/* Можеш да добавиш целия текст от предишния компонент */}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenTerms(false)} variant="contained">Затвори</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
